// Trade ideas: written in Morning prep (product, direction, trigger, stop, target, why), tracked
// in the Live session (Took it / Skipped / Didn't trigger) and reviewed in the reflection. A trade
// in the idea's product (and direction) that day marks it "Took it" by itself (sessionLinks.js).
import { useState } from 'react';
import { useI18n } from './i18n';
import { dayKey, formatMoney, netPnl } from './pnl';

export const IDEA_STATUSES = [
  ['planned', 'Waiting'], ['took', 'Took it'], ['skipped', 'Skipped'], ['missed', 'Didn’t trigger'],
];
const MAX_IDEAS = 10;
const newId = () => Math.random().toString(36).slice(2, 12);
const EMPTY = { symbol: '', side: '', trigger: '', stop: '', target: '', why: '' };

function IdeaForm({ start, onSave, onCancel }) {
  const { t } = useI18n();
  const [idea, setIdea] = useState({ ...EMPTY, ...start });
  const set = (k, v) => setIdea((x) => ({ ...x, [k]: v }));
  const ok = idea.symbol.trim() || idea.trigger.trim() || idea.why.trim();
  return (
    <form className="idea-form" onSubmit={(e) => { e.preventDefault(); if (ok) onSave({ ...idea, symbol: idea.symbol.trim().toUpperCase() }); }}>
      <div className="idea-form-row">
        <label className="idea-field idea-symbol">
          <span>{t('Product')}</span>
          <input autoFocus value={idea.symbol} maxLength={20} placeholder="MNQ" onChange={(e) => set('symbol', e.target.value.toUpperCase())} />
        </label>
        <div className="idea-field">
          <span>{t('Direction')}</span>
          <div className="idea-side" role="group" aria-label={t('Direction')}>
            {['Long', 'Short'].map((side) => (
              <button key={side} type="button" className={`secondary small${idea.side === side ? ' active' : ''}`} aria-pressed={idea.side === side}
                onClick={() => set('side', idea.side === side ? '' : side)}>{t(side)}</button>
            ))}
          </div>
        </div>
        <label className="idea-field idea-short">
          <span>{t('Stop')}</span>
          <input value={idea.stop} maxLength={40} placeholder="21,080" onChange={(e) => set('stop', e.target.value)} />
        </label>
        <label className="idea-field idea-short">
          <span>{t('Target')}</span>
          <input value={idea.target} maxLength={40} placeholder="21,180" onChange={(e) => set('target', e.target.value)} />
        </label>
      </div>
      <label className="idea-field">
        <span>{t('Trigger')}</span>
        <input value={idea.trigger} maxLength={200} placeholder={t('e.g. Pullback to VWAP holds, first green 1-min candle')} onChange={(e) => set('trigger', e.target.value)} />
      </label>
      <label className="idea-field">
        <span>{t('Why')}</span>
        <input value={idea.why} maxLength={500} placeholder={t('e.g. Trend day, above the opening range')} onChange={(e) => set('why', e.target.value)} />
      </label>
      <div className="idea-form-actions">
        <button type="submit" className="small" disabled={!ok}>{start && start.id ? t('Save idea') : t('Add idea')}</button>
        <button type="button" className="link-button" onClick={onCancel}>{t('Cancel')}</button>
      </div>
    </form>
  );
}

function IdeaHead({ idea }) {
  const { t } = useI18n();
  return (
    <div className="idea-head">
      {idea.symbol && <strong className="idea-sym">{idea.symbol}</strong>}
      {idea.side && <span className={`nb-side ${idea.side === 'Short' ? 'short' : 'long'}`}>{t(idea.side)}</span>}
      {idea.trigger && <span className="idea-trigger">{idea.trigger}</span>}
    </div>
  );
}

function IdeaFacts({ idea }) {
  const { t } = useI18n();
  const facts = [idea.stop && `${t('Stop')} ${idea.stop}`, idea.target && `${t('Target')} ${idea.target}`].filter(Boolean);
  return (
    <>
      {facts.length > 0 && <span className="idea-facts">{facts.join(' · ')}</span>}
      {idea.why && <span className="idea-why">{idea.why}</span>}
    </>
  );
}

function LinkedTrade({ idea, trades, onOpenTrade }) {
  const { t } = useI18n();
  const trade = idea.tradeId && trades.find((x) => String(x._id) === String(idea.tradeId));
  if (!trade) return null;
  const net = netPnl(trade);
  return (
    <button type="button" className="idea-trade" onClick={() => onOpenTrade && onOpenTrade(trade)} disabled={!onOpenTrade}>
      🔗 {t('Trade')} {trade.symbol} · <span className={net > 0 ? 'gain' : net < 0 ? 'loss' : ''}>{formatMoney(net)}</span>
    </button>
  );
}

// Morning prep: write the day's ideas.
export function TradeIdeasPlan({ ideas = [], onChange }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(null);
  const save = (idea) => {
    onChange(idea.id ? ideas.map((x) => (x.id === idea.id ? { ...x, ...idea } : x)) : [...ideas, { ...idea, id: newId(), status: 'planned' }]);
    setEditing(null);
  };
  return (
    <div className="routine-block trade-ideas">
      <div className="routine-block-head">
        <span className="routine-block-title">{t('My trade ideas for today')}</span>
        <span className="field-hint">{t('{n} of {max}', { n: ideas.length, max: MAX_IDEAS })}</span>
      </div>
      {ideas.length === 0 && editing === null && (
        <p className="sidebar-muted">{t('Write the 1 to 3 trades you’re waiting for: product, direction, the trigger, stop and target. The Live session keeps them in front of you.')}</p>
      )}
      <ul className="idea-list">
        {ideas.map((idea) => (editing === idea.id ? (
          <li key={idea.id}><IdeaForm start={idea} onSave={save} onCancel={() => setEditing(null)} /></li>
        ) : (
          <li key={idea.id} className="idea-card">
            <IdeaHead idea={idea} />
            <IdeaFacts idea={idea} />
            <span className="idea-actions">
              <button type="button" className="link-button" onClick={() => setEditing(idea.id)}>{t('Edit')}</button>
              <button type="button" className="link-button danger-link" onClick={() => onChange(ideas.filter((x) => x.id !== idea.id))}>{t('Remove')}</button>
            </span>
          </li>
        )))}
      </ul>
      {editing === 'new' ? <IdeaForm onSave={save} onCancel={() => setEditing(null)} />
        : ideas.length < MAX_IDEAS && <button type="button" className="secondary small" onClick={() => setEditing('new')}>+ {t('Add a trade idea')}</button>}
    </div>
  );
}

function StatusButtons({ idea, onSet }) {
  const { t } = useI18n();
  return (
    <div className="idea-status" role="group" aria-label={t('What happened with this idea?')}>
      {IDEA_STATUSES.filter(([id]) => id !== 'planned').map(([id, label]) => (
        <button key={id} type="button" className={`secondary small${idea.status === id ? ' active' : ''}`} aria-pressed={idea.status === id}
          onClick={() => onSet(idea.status === id ? 'planned' : id)}>{t(label)}</button>
      ))}
    </div>
  );
}

// Live session: the ideas as a watchlist.
export function TradeIdeasWatch({ ideas = [], onChange, trades = [] }) {
  const { t } = useI18n();
  if (!ideas.length) return null;
  const setStatus = (id, status) => onChange(ideas.map((x) => (x.id === id ? { ...x, status } : x)));
  return (
    <div className="routine-block trade-ideas watch">
      <div className="routine-block-head">
        <span className="routine-block-title">{t('Watching for')}</span>
        <span className="field-hint">{t('From your Morning prep')}</span>
      </div>
      <ul className="idea-list">
        {ideas.map((idea) => (
          <li key={idea.id} className={`idea-card status-${idea.status}`}>
            <IdeaHead idea={idea} />
            <IdeaFacts idea={idea} />
            <LinkedTrade idea={idea} trades={trades} />
            <StatusButtons idea={idea} onSet={(status) => setStatus(idea.id, status)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Reflection: how each idea played out.
export function TradeIdeasReview({ ideas = [], onChange, trades = [], onOpenTrade }) {
  const { t } = useI18n();
  if (!ideas.length) return null;
  const set = (id, changes) => onChange(ideas.map((x) => (x.id === id ? { ...x, ...changes } : x)));
  return (
    <div className="routine-block trade-ideas review">
      <span className="routine-block-title">{t('Your trade ideas')}</span>
      <ul className="idea-list">
        {ideas.map((idea) => (
          <li key={idea.id} className={`idea-card status-${idea.status}`}>
            <IdeaHead idea={idea} />
            <IdeaFacts idea={idea} />
            <LinkedTrade idea={idea} trades={trades} onOpenTrade={onOpenTrade} />
            <StatusButtons idea={idea} onSet={(status) => set(idea.id, { status })} />
            <input type="text" className="idea-result" value={idea.result || ''} maxLength={500}
              onChange={(e) => set(idea.id, { result: e.target.value })}
              placeholder={idea.status === 'skipped' ? t('Good skip, or a missed chance?') : idea.status === 'took' ? t('Did it go to plan?') : t('How did it play out?')}
              aria-label={t('How did it play out?')} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Trades that came from an idea vs the rest, over the given day notes. Null when no ideas yet.
export function ideaStats(trades, notes) {
  const ideaTrades = new Set();
  let ideas = 0;
  let skipped = 0;
  for (const n of Object.values(notes || {})) {
    for (const i of n.ideas || []) {
      ideas += 1;
      if (i.status === 'skipped') skipped += 1;
      if (i.tradeId) ideaTrades.add(String(i.tradeId));
    }
  }
  if (!ideas) return null;
  const days = new Set(Object.values(notes || {}).filter((n) => (n.ideas || []).length).map((n) => n.date));
  const sum = (list) => ({ count: list.length, net: list.reduce((a, x) => a + netPnl(x), 0), wins: list.filter((x) => netPnl(x) > 0).length });
  const onIdeaDays = trades.filter((x) => days.has(dayKey(x.date)));
  return {
    ideas, skipped,
    planned: sum(onIdeaDays.filter((x) => ideaTrades.has(String(x._id)))),
    unplanned: sum(onIdeaDays.filter((x) => !ideaTrades.has(String(x._id)))),
  };
}

// Insights: trades that came from a planned idea vs the rest, on days with ideas.
export function IdeasCard({ trades, notes }) {
  const { t } = useI18n();
  const s = ideaStats(trades, notes);
  if (!s || s.planned.count + s.unplanned.count < 3) return null;
  const row = (label, x) => (
    <div className="ideas-card-row">
      <span>{label}</span>
      <strong className={x.net > 0 ? 'gain' : x.net < 0 ? 'loss' : ''}>{formatMoney(x.net)}</strong>
      <span className="field-hint">{t('{n} trades', { n: x.count })}{x.count ? ` · ${t('Win rate')} ${Math.round((x.wins / x.count) * 100)}%` : ''}</span>
    </div>
  );
  const better = s.planned.count && s.unplanned.count && s.planned.net / s.planned.count > s.unplanned.net / s.unplanned.count;
  return (
    <section className="ideas-card">
      <h2>{t('Planned vs unplanned trades')}</h2>
      <p className="field-hint">{t('On days you wrote trade ideas in Morning prep.')}</p>
      {row(t('From your ideas'), s.planned)}
      {row(t('Not in your plan'), s.unplanned)}
      {s.planned.count > 0 && s.unplanned.count > 0 && (
        <p className="ideas-card-verdict">
          {better ? t('Your planned trades earn more per trade. Wait for your ideas.') : t('Your unplanned trades did as well or better. Check whether your ideas are too strict, or got lucky.')}
        </p>
      )}
    </section>
  );
}
